// src/components/farm/FarmProductCard.js
// Unified product card used by marketplace and profile surfaces.
// Marketplace proportions as the default visual baseline.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Edit3, Eye, EyeOff, Flag, Heart, MoreVertical, Trash2 } from 'lucide-react';
import { extractExpiryDate, formatExpirationMulti } from '../../utils/formatExpiration';
import { errorDev } from '../../utils/logger.js';

export default function FarmProductCard({
  product,
  compact = false,
  isOwner = false,
  isWished = false,
  wishCount = 0,
  onToggleWishlist,
  onEdit,
  onHide,
  onDelete,
  onReport,
}) {
  const { t } = useTranslation('farmMarketplace');
  const navigate = useNavigate();

  const img = product.images?.[0] || product.image || product.thumbnail || '/images/placeholder.jpg';
  const title = product.title || product.name || '';
  const price = formatPricePerKg(product.pricePerKg ?? product.price, product.currency);
  const quantityLabel = formatQuantityKg(product.quantity, product.quantityUnit || product.unit);
  const location = formatLocationShort(product.location);
  const category = product.category || null;
  const productState = product.productState || product.state || null;
  const storageCondition = product.storageCondition || null;

  const storageLabel = useMemo(() => {
    if (productState) {
      const key = `productState.${productState}`;
      const val = t(key);
      return val !== key ? val : capitalize(productState);
    }
    if (storageCondition) {
      const key = `storageCondition.${storageCondition}`;
      const val = t(key);
      return val !== key ? val : capitalize(storageCondition);
    }
    return null;
  }, [productState, storageCondition, t]);

  // Expiration — multi-unit: "2M 1W", "3W 2D", "5D 12H", etc.
  const expiryDate = useMemo(() => extractExpiryDate(product), [product]);
  const expirationLabel = useMemo(() => {
    const res = formatExpirationMulti(expiryDate);
    if (!res) return null;
    if (res === 'expired') return t('expired');
    return res;
  }, [expiryDate, t]);

  const isExpired = expirationLabel === t('expired');
  const isSold = product.sold === true;
  const isHidden = product.hidden === true || product.isHidden === true || product.isPrivate === true;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClickAway = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    window.addEventListener('mousedown', onClickAway);
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('mousedown', onClickAway);
      window.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  const goDetail = () => navigate(`/product/${product.id}`);

  // Build the subtitle line: "Category · Storage" or just one
  const subtitle = [category && capitalize(category), storageLabel]
    .filter(Boolean)
    .join(' · ') || null;

  return (
    <article
      onClick={goDetail}
      className="cursor-pointer bg-white dark:bg-neutral-900 rounded-lg overflow-hidden border border-gray-100 dark:border-neutral-800 hover:shadow-md transition-shadow flex flex-col"
    >
      {/* ── Square Image ── */}
      <div className="relative aspect-square bg-gray-100 dark:bg-neutral-800 overflow-hidden">
        <img
          src={img}
          alt={title}
          className={`w-full h-full object-cover ${isSold || isExpired || isHidden ? 'opacity-50 grayscale-[30%]' : ''}`}
          loading="lazy"
          onError={(e) => { e.currentTarget.src = '/images/placeholder.jpg'; }}
        />

        {/* Status badge — top-left */}
        {(isHidden || isSold || isExpired) && (
          <div className="absolute top-1.5 left-1.5 flex flex-wrap gap-1">
            {isHidden && isOwner && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider text-yellow-900 bg-yellow-300/90">
                {`\u26A0 ${t('common:hidden')}`}
              </span>
            )}
            {isSold && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider text-white bg-gray-800/80">
                {t('sold')}
              </span>
            )}
            {isExpired && (
              <span className="text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider text-white bg-red-600/85">
                {t('expired')}
              </span>
            )}
          </div>
        )}

        {/* Owner menu */}
        {isOwner && (onEdit || onHide || onDelete) && (
          <div className="absolute top-1.5 right-1.5 z-20">
            <button
              ref={menuButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((prev) => !prev);
              }}
              className="p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white backdrop-blur-sm"
              aria-label={t('actions.title', { ns: 'marketplace', defaultValue: 'Actions' })}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
            >
              <MoreVertical className="w-3.5 h-3.5" />
            </button>

            {menuOpen && (
              <div
                ref={menuRef}
                role="menu"
                className="absolute right-0 mt-1 w-40 rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-xl overflow-hidden"
              >
                <button
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onEdit?.(product);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-neutral-800 flex items-center gap-2"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                  {t('common:edit')}
                </button>
                <button
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onHide?.(product);
                  }}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-neutral-800 flex items-center gap-2"
                >
                  {isHidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                  {isHidden ? t('common:visible') : t('common:hidden')}
                </button>
                <button
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuOpen(false);
                    onDelete?.(product);
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 flex items-center gap-2"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('common:delete')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Non-owner report */}
        {!isOwner && typeof onReport === 'function' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onReport(product);
            }}
            className="absolute top-1.5 right-1.5 z-20 p-1.5 rounded-full bg-black/40 hover:bg-red-600 text-white backdrop-blur-sm transition"
            aria-label={t('common:report', { defaultValue: 'Report' })}
            title={t('common:report', { defaultValue: 'Report' })}
          >
            <Flag className="w-3.5 h-3.5" />
          </button>
        )}

        {/* Expiration — bottom-left on image */}
        {expirationLabel && !isExpired && (
          <span className="absolute bottom-1.5 left-1.5 text-[10px] px-1.5 py-0.5 rounded bg-black/55 text-white font-medium backdrop-blur-sm">
            {expirationLabel}
          </span>
        )}

        {/* Wishlist heart + count — bottom-right */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleWishlist?.(product, e);
          }}
          className={`absolute bottom-1.5 right-1.5 inline-flex items-center gap-0.5 px-1.5 py-1 rounded-full text-[11px] font-medium transition-all ${
            isWished
              ? 'bg-green-600 text-white'
              : 'bg-white/70 text-gray-500 dark:bg-neutral-800/70 dark:text-gray-400 backdrop-blur-sm'
          }`}
          aria-label={isWished ? t('wishlistRemoved') : t('wishlistAdded')}
        >
          <Heart className={`w-3.5 h-3.5 ${isWished ? 'fill-current' : ''}`} />
          {wishCount > 0 && <span>{wishCount}</span>}
        </button>
      </div>

      {/* ── Text Section ── */}
      <div
        className={compact
          ? 'px-1.5 pt-1 pb-1.5 flex flex-col gap-0.5 min-h-[56px] sm:min-h-[64px]'
          : 'px-2 pt-1.5 pb-2 flex flex-col gap-0.5 min-h-[60px] sm:min-h-[72px]'
        }
      >
        {/* Price */}
        <p className={compact
          ? 'text-[13px] sm:text-sm font-bold text-gray-900 dark:text-white leading-tight'
          : 'text-sm sm:text-[15px] font-bold text-gray-900 dark:text-white leading-tight'
        }>
          {quantityLabel ? `${price} \u00B7 ${quantityLabel}` : price}
        </p>

        {/* Product name */}
        <h3 className={compact
          ? 'text-[11px] sm:text-[12px] font-medium text-gray-700 dark:text-gray-200 leading-snug line-clamp-1'
          : 'text-[12px] sm:text-[13px] font-medium text-gray-700 dark:text-gray-200 leading-snug line-clamp-1'
        }>
          {title || 'Untitled'}
        </h3>

        {/* Category · Storage */}
        {subtitle && (
          <p className="text-[10px] sm:text-[11px] text-gray-400 dark:text-gray-500 truncate">
            {subtitle}
          </p>
        )}

        {/* Location */}
        {location && (
          <p className="text-[10px] sm:text-[11px] text-gray-400 dark:text-gray-500 truncate mt-auto">
            {location}
          </p>
        )}
      </div>
    </article>
  );
}

// ── Helpers ──

function formatPricePerKg(value, currency = 'RWF') {
  if (value === 0 || value === '0') return 'Free';
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value} ${currency}`;
  try {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n) + ` ${currency}`;
  } catch (err) {
    errorDev("Operation failed:", err);
    return `${n} ${currency}`;
  }
}

function formatQuantityKg(quantity, unit) {
  const n = Number(quantity);
  if (!Number.isFinite(n) || n <= 0) return null;
  const normalizedUnit = String(unit || 'kg').toUpperCase();
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(n)} ${normalizedUnit}`;
}

function formatLocationShort(loc) {
  if (!loc || typeof loc !== 'object') return null;
  const parts = [
    loc.sector || loc.Sector,
    loc.district || loc.District || loc.city,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

function capitalize(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
}
